<script setup lang="ts">
import { Quill, QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { NDropdown, NSpin, useMessage } from "naive-ui";
import type { QuillOptionsStatic } from "quill";
import { computed, h, onMounted, onUnmounted, ref, watch } from "vue";
import { uploadPublicImage } from "../services/cms.ts";
import { getFileSizeString } from "../utils/file";
import { stripHtmlTags } from "../utils/strings.ts";
import { usePortalBreakpoints } from "../hooks/usePortalBreakpoints.ts";
const { isDesktop } = usePortalBreakpoints();

const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024; // 20MB in bytes

interface MentionOption {
    label: string;
    value: string;
}

const props = withDefaults(
    defineProps<{
        placeholder?: string;
        buttonText?: string;
        minRows?: number;
        submitDisabled?: boolean;
        initialValue?: string;
        fileList?: File[] | null;
        showFileUpload?: boolean;
        modelValue?: string;
        showSubmitButton?: boolean;
        isSubmitting?: boolean;
        enableMentions?: boolean;
        mentionOptions?: MentionOption[];
    }>(),
    {
        placeholder: "Enter message...",
        buttonText: "Send",
        minRows: 3,
        submitDisabled: false,
        initialValue: "",
        fileList: null,
        showFileUpload: true,
        modelValue: "",
        showSubmitButton: true,
        isSubmitting: false,
        enableMentions: false,
        mentionOptions: () => [],
    },
);

const emit = defineEmits<{
    "update:value": [value: string];
    "update:fileList": [files?: File[]];
    submit: [];
    "update:modelValue": [value: string];
}>();

const message = useMessage();

const showMentionDropdown = ref(false);
const mentionDropdownX = ref(0);
const mentionDropdownY = ref(0);
const mentionSearch = ref("");
const mentionStartIndex = ref(0);
const selectedIndex = ref(0);
const fileInput = ref<HTMLInputElement>();
const localValue = ref(props.modelValue || props.initialValue);
const editorRef = ref();

// Mentions

const filteredMentions = computed(() => {
    if (!mentionSearch.value) {
        return props.mentionOptions || [];
    }

    return (props.mentionOptions || []).filter(
        (option) =>
            option.label.toLowerCase().includes(mentionSearch.value.toLowerCase()) ||
            option.value.toLowerCase().includes(mentionSearch.value.toLowerCase()),
    );
});

function handleMentionSelect(mention: string) {
    const quill = editorRef.value?.getQuill();
    if (!quill) {
        return;
    }

    const mentionText = `@${mention}`;
    quill.deleteText(mentionStartIndex.value - 1, mentionSearch.value.length + 1);

    quill.insertText(mentionStartIndex.value - 1, mentionText, {
        color: "#0066cc",
        background: "rgba(0, 122, 255, 0.1)",
    });

    quill.insertText(mentionStartIndex.value - 1 + mentionText.length, " ", {
        color: null,
        background: null,
    });

    const newPosition = mentionStartIndex.value - 1 + mentionText.length + 1;
    quill.setSelection(newPosition, 0);

    showMentionDropdown.value = false;
    mentionSearch.value = "";
}

function handleTextChange() {
    const quill = editorRef.value?.getQuill();
    if (!quill) {
        return;
    }

    const selection = quill.getSelection();
    if (!selection) {
        return;
    }

    const [leaf] = quill.getLeaf(selection.index);
    const text = leaf.text || "";

    const currentPosition = selection.index;
    const textBeforeCursor = text.slice(0, currentPosition);

    // Mentions prefixed with @
    const matches = textBeforeCursor.match(/@([^@]*?)$/);

    if (matches) {
        const rect = quill.getBounds(currentPosition);
        const editorBounds = quill.container.getBoundingClientRect();

        mentionStartIndex.value = currentPosition - (matches[1]?.length || 0);
        mentionSearch.value = matches[1] || "";
        mentionDropdownX.value = editorBounds.left + rect.left;
        mentionDropdownY.value = editorBounds.top + rect.top + rect.height;
        showMentionDropdown.value = true;
    } else {
        showMentionDropdown.value = false;
    }
}

function focusInput() {
    const quill = editorRef.value?.getQuill();
    if (quill) {
        quill.focus();
    }
}

function onUpdateContent(content: string) {
    localValue.value = content;
    emit("update:value", content);
    emit("update:modelValue", content);
    handleTextChange();
}

function onFileInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.files) {
        return;
    }

    const newAttachments = Array.from(target.files);
    const currentAttachments = props.fileList ?? [];
    const updatedAttachments = [...currentAttachments, ...newAttachments];

    // Calculate total attachment size
    const totalSize = updatedAttachments.reduce((sum, file) => sum + file.size, 0);

    if (totalSize > MAX_ATTACHMENT_SIZE) {
        message.error(
            `Attachments exceed the 20MB limit. Current total: ${getFileSizeString(totalSize)}`,
        );
        return;
    }

    emit("update:fileList", updatedAttachments);
}

function removeFile(index: number) {
    const newAttachments = [...(props.fileList ?? [])];
    newAttachments.splice(index, 1);
    emit("update:fileList", newAttachments);
}

async function handleImageSelection(): Promise<File | null> {
    return new Promise((resolve) => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");

        input.onchange = () => {
            const file = input.files?.[0] ?? null;
            input.remove();
            resolve(file);
        };

        input.oncancel = () => {
            input.remove();
            resolve(null);
        };

        input.click();
    });
}

const options = computed<QuillOptionsStatic>(() => ({
    theme: "snow",
    bounds: document.body,
    modules: {
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                props.showFileUpload ? ["link", "image", "attachment"] : ["link", "image"],
            ],
            handlers: {
                attachment: () => {
                    fileInput.value?.click();
                },
                image: async () => {
                    const file = await handleImageSelection();
                    if (!file) {
                        return;
                    }
                    const quill = editorRef.value?.getQuill();
                    if (quill) {
                        await handleImageUpload(file, quill);
                    }
                },
            },
        },
    },
    placeholder: props.placeholder,
}));

async function handleImageUpload(file: File, quill: Quill) {
    // Check attachment size limit
    const currentAttachments = props.fileList ?? [];
    const updatedAttachments = [...currentAttachments, file];
    const totalSize = updatedAttachments.reduce((sum, f) => sum + f.size, 0);

    if (totalSize > MAX_ATTACHMENT_SIZE) {
        message.error(
            `Attachments exceed the 20MB limit. Current total: ${getFileSizeString(totalSize)}`,
        );
        return;
    }

    const loadingMessage = message.loading("Uploading image...", {
        duration: 0,
    });

    try {
        const url = await uploadPublicImage(file);
        loadingMessage.destroy();
        message.success("Image uploaded successfully");

        emit("update:fileList", updatedAttachments);

        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", url);
        quill.setSelection({ index: range.index + 1, length: 0 });
    } catch (error) {
        loadingMessage.destroy();
        message.error(error instanceof Error ? error.message : "Failed to upload image");
        console.error("Failed to upload image:", error);
    }
}

function handleDrop(event: DragEvent) {
    event.preventDefault();
    if (!event.dataTransfer?.files.length) {
        return;
    }

    const file = event.dataTransfer.files[0];
    const currentAttachments = props.fileList ?? [];
    const updatedAttachments = [...currentAttachments, file];

    // Calculate total attachment size
    const totalSize = updatedAttachments.reduce((sum, f) => sum + f.size, 0);

    if (totalSize > MAX_ATTACHMENT_SIZE) {
        message.error(
            `Attachments exceed the 20MB limit. Current total: ${getFileSizeString(totalSize)}`,
        );
        return;
    }

    // Handle images by embedding them in the editor
    if (file.type.startsWith("image/")) {
        const quill = editorRef.value?.getQuill();
        if (quill) {
            handleImageUpload(file, quill);
        }
    } else {
        // For non-image files, add them to file attachments
        emit("update:fileList", updatedAttachments);
    }
}

function handlePaste(e: ClipboardEvent) {
    const quill = editorRef.value?.getQuill();
    if (!quill) {
        return;
    }

    const items = e.clipboardData?.items;
    if (!items) {
        return;
    }

    for (const item of items) {
        if (item.kind === "file") {
            e.preventDefault();
            const file = item.getAsFile();
            if (file) {
                // Handle images directly in the editor
                if (file.type.startsWith("image/")) {
                    handleImageUpload(file, quill);
                } else {
                    // Handle other file types as attachments
                    const currentAttachments = props.fileList ?? [];
                    const updatedAttachments = [...currentAttachments, file];

                    const totalSize = updatedAttachments.reduce((sum, f) => sum + f.size, 0);
                    if (totalSize > MAX_ATTACHMENT_SIZE) {
                        message.error(
                            `Attachments exceed the 20MB limit. Current total: ${getFileSizeString(
                                totalSize,
                            )}`,
                        );
                        return;
                    }

                    emit("update:fileList", updatedAttachments);
                }
            }
            break;
        }
    }
}

watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue !== undefined && editorRef.value) {
            const quill = editorRef.value.getQuill();
            if (quill.root.innerHTML !== newValue) {
                quill.root.innerHTML = newValue;
                localValue.value = newValue;
            }
        }
    },
);

onMounted(() => {
    localValue.value = props.initialValue;

    if (props.showFileUpload) {
        fileInput.value = document.createElement("input");
        fileInput.value.type = "file";
        fileInput.value.multiple = true;
        fileInput.value.style.display = "none";
        fileInput.value.onchange = onFileInputChange;
        document.body.appendChild(fileInput.value);
    }

    const quill = editorRef.value?.getQuill();
    if (quill) {
        const editableDiv = quill.root;
        editableDiv.addEventListener(
            "paste",
            (e: ClipboardEvent) => {
                handlePaste(e);
            },
            true,
        );
    }
});

onUnmounted(() => {
    fileInput.value?.remove();

    const quill = editorRef.value?.getQuill();
    if (quill) {
        quill.root.removeEventListener("paste", handlePaste, true);
    }
});

defineExpose({
    focusInput,
});
</script>

<template>
    <div class="flex flex-col gap-4 text-left">
        <div
            class="rich-text-editor relative"
            @drop="handleDrop"
            @dragover="(e) => e.preventDefault()"
        >
            <quill-editor
                ref="editorRef"
                :content="localValue"
                :options="options"
                content-type="html"
                @update:content="onUpdateContent"
            />
            <n-dropdown
                v-if="showMentionDropdown && props.enableMentions"
                size="small"
                :show="showMentionDropdown"
                :options="
                    filteredMentions.map((option, index) => ({
                        key: option.value,
                        label: option.label,
                        value: option.value,
                        render: () =>
                            h(
                                'div',
                                {
                                    class: [
                                        'flex items-center gap-2 p-2',
                                        index === selectedIndex ? 'bg-primary-100' : '',
                                    ],
                                },
                                [h('span', { class: 'font-medium' }, option.label)],
                            ),
                    }))
                "
                :menu-props="
                    () => ({
                        style: 'max-height: 200px; overflow: auto;',
                    })
                "
                :x="mentionDropdownX"
                :y="mentionDropdownY"
                :on-clickoutside="
                    () => {
                        showMentionDropdown = false;
                    }
                "
                placement="bottom-start"
                @select="handleMentionSelect"
            />
        </div>

        <!-- File attachments list -->
        <div v-if="fileList?.length" class="attachments">
            <div
                v-for="(file, i) in fileList"
                :key="`${file.name}-${i}`"
                class="transition-color-opacity duration-350 py-1 px-2 w-full flex bg-cream-dark rounded justify-between items-center center mb-2 relative overflow-hidden hover:bg-cream-dark"
            >
                <div class="font-mono text-dark break-all text-[10px] uppercase">
                    {{ file.name }}
                    <span class="text-gray-dark pl-1">({{ getFileSizeString(file.size) }})</span>
                </div>
                <span
                    class="material-symbols-outlined text-base cursor-pointer"
                    @click="removeFile(i)"
                >
                    close
                </span>
            </div>
        </div>

        <!-- Submit button -->
        <div v-if="showSubmitButton" class="flex justify-end gap-2">
            <slot />
            <button
                :disabled="
                    submitDisabled ||
                    !((stripHtmlTags(localValue).trim() || /<img\b/i.test(localValue)) || fileList?.length)
                "
                class="button"
                :class="{
                    'button-mobile': !isDesktop,
                }"
                @click="emit('submit')"
            >
                {{ buttonText }}
                <n-spin v-if="isSubmitting" size="small" class="ml-2" />
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
.rich-text-editor {
    border: 1px solid rgba(16, 16, 20, 0.15);
    border-radius: 8px;

    :deep(.ql-toolbar) {
        // background-color: #f8f9f3;
        border-top-left-radius: 0.75rem;
        border-top-right-radius: 0.75rem;
        border: 0;
        // border-bottom: 1px solid rgba(16, 16, 20, 0.15);

        .ql-attachment:after {
            content: "attach_file";
            font-family: "Material Symbols Outlined";
            font-size: 18px;
            line-height: 21px;
        }
    }

    :deep(.ql-container) {
        // background-color: #f4f5ef;
        border-bottom-left-radius: 0.75rem;
        border-bottom-right-radius: 0.75rem;
        border: 0;
    }

    :deep(.ql-editor) {
        min-height: v-bind("`${Math.max(props.minRows * 24, 100)}px`");
        padding: 0.75rem;
    }

    :deep(.ql-tooltip) {
        position: absolute;
        left: 50% !important;
        top: 50% !important;
        transform: translate(-50%, -50%) !important;
        background-color: white;

        input[type="text"] {
            background-color: white;
        }
    }
}

:deep(.mention) {
    background-color: rgba(0, 122, 255, 0.1);
    border-radius: 4px;
    padding: 2px 4px;
    color: #0066cc;
    white-space: nowrap;
}
</style>
